// MyConsumptionStatsModal.jsx — "Mes statistiques" : combien LE CLIENT a généré et consommé
// en Ariary (vidéo + image). Trois vues : vue d'ensemble (jour/semaine/mois/période), par date
// (mêmes générations mais détaillées jour par jour avec le moteur utilisé) et, côté Video
// Studio uniquement, par storyboard/film (demande explicite de Hery 2026-09-24 : "nombre de
// génération, outils utilisés, total prix généré" — par titre pour les storyboards, par date
// pour la génération normale). Version client (scope à son propre compte) du dashboard admin
// équivalent - demande explicite de Hery le 2026-08-20 : "sur chaque compte pour chaque client".

import React, { useState, useEffect, useCallback } from 'react';
import {
  X, Coins, Film, ImageIcon, Loader2, Calendar, RefreshCw, TrendingDown, TrendingUp, BarChart3,
  LayoutGrid, Clapperboard, CheckCircle2,
} from 'lucide-react';
import { API_URL } from '../../config';

const toISODate = (d) => d.toISOString().slice(0, 10);
const today = () => new Date();
const formatAr = (n) => `${(n || 0).toLocaleString('fr-FR')} Ar`;

const ENGINE_LABELS = {
  grok: 'TeckiA G', omni: 'TeckiA O', seedance_mini: 'TeckiA SM',
  veo_lite: 'TeckiA V Lite', veo_fast: 'TeckiA V Fast', veo_quality: 'TeckiA V Quality',
  infinitalk: 'TeckiA Lip Sync', motion_control: 'TeckiA Motion',
  nanobanana: 'TeckiA Pro Image', gpt_best: 'TeckiA Best Image',
  inconnu: 'Autre',
};
const engineLabel = (key) => ENGINE_LABELS[key] || key;

const RANGE_PRESETS = [
  { id: 'today', label: "Aujourd'hui", range: () => { const t = today(); return [toISODate(t), toISODate(t)]; } },
  {
    id: 'week', label: 'Cette semaine',
    range: () => {
      const t = today();
      const day = (t.getDay() + 6) % 7;
      const monday = new Date(t); monday.setDate(t.getDate() - day);
      return [toISODate(monday), toISODate(t)];
    },
  },
  {
    id: 'month', label: 'Ce mois',
    range: () => { const t = today(); return [toISODate(new Date(t.getFullYear(), t.getMonth(), 1)), toISODate(t)]; },
  },
  { id: 'custom', label: 'Personnalisé', range: null },
];

const EMPTY = { count: 0, charged_ar: 0, refunded_ar: 0, net_ar: 0 };

function StatCard({ icon: Icon, title, count, netAr, gradient }) {
  return (
    <div className={`relative overflow-hidden rounded-2xl p-4 text-white bg-gradient-to-br ${gradient}`}>
      <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-white/10 blur-2xl" />
      <div className="relative z-10">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-8 h-8 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Icon className="w-4 h-4" />
          </div>
          <div className="font-bold text-sm">{title}</div>
        </div>
        <div className="text-2xl font-black">{netAr.toLocaleString('fr-FR')} <span className="text-sm font-bold">Ar</span></div>
        <div className="text-white/80 text-xs mt-0.5">{count} génération{count > 1 ? 's' : ''}</div>
      </div>
    </div>
  );
}

function EngineChips({ byEngine }) {
  const entries = Object.entries(byEngine || {});
  if (entries.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5 mt-2">
      {entries.map(([engine, e]) => (
        <span key={engine} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-white/5 border border-white/10 text-[10px] font-semibold text-gray-300">
          {engineLabel(engine)} <span className="text-gray-500">×{e.count}</span>
          <span className="text-amber-300/80">· {formatAr(e.total_ar)}</span>
        </span>
      ))}
    </div>
  );
}

function DayCard({ day }) {
  const total = day.video.total_ar + day.image.total_ar;
  const count = day.video.count + day.image.count;
  if (count === 0) return null;
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-3.5">
      <div className="flex items-center justify-between mb-2.5">
        <div className="flex items-center gap-1.5 text-white font-bold text-sm">
          <Calendar className="w-3.5 h-3.5 text-amber-300" />
          {new Date(day.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' })}
        </div>
        <div className="text-amber-300 font-bold text-sm">{formatAr(total)}</div>
      </div>
      <div className="grid sm:grid-cols-2 gap-2.5">
        {day.video.count > 0 && (
          <div className="bg-black/20 rounded-lg p-2.5">
            <div className="flex items-center gap-1.5 text-[11px] font-bold text-fuchsia-300 mb-1">
              <Film className="w-3 h-3" /> Vidéo — {day.video.count} · {formatAr(day.video.total_ar)}
            </div>
            <EngineChips byEngine={day.video.by_engine} />
          </div>
        )}
        {day.image.count > 0 && (
          <div className="bg-black/20 rounded-lg p-2.5">
            <div className="flex items-center gap-1.5 text-[11px] font-bold text-cyan-300 mb-1">
              <ImageIcon className="w-3 h-3" /> Image — {day.image.count} · {formatAr(day.image.total_ar)}
            </div>
            <EngineChips byEngine={day.image.by_engine} />
          </div>
        )}
      </div>
    </div>
  );
}

function StoryboardCard({ item }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-3.5">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5 text-white font-bold text-sm min-w-0">
          <Clapperboard className="w-3.5 h-3.5 text-amber-300 flex-shrink-0" />
          <span className="truncate">{item.title}</span>
        </div>
        <div className="text-amber-300 font-bold text-sm flex-shrink-0">{formatAr(item.total_ar)}</div>
      </div>
      <div className="flex items-center gap-3 text-[11px] text-gray-400 mb-1">
        <span className="flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-400" /> {item.completed_count}/{item.scene_count} scène{item.scene_count > 1 ? 's' : ''}</span>
        {item.final_video_url && <span className="text-emerald-400 font-semibold">Assemblé</span>}
      </div>
      <EngineChips byEngine={item.by_engine} />
    </div>
  );
}

export default function MyConsumptionStatsModal({ onClose, showStoryboards = false }) {
  const token = localStorage.getItem('auth_token');
  const authHeaders = { Authorization: `Bearer ${token}` };
  const [tab, setTab] = useState('overview'); // 'overview' | 'by-date' | 'by-storyboard'
  const [preset, setPreset] = useState('today');
  const [customFrom, setCustomFrom] = useState(toISODate(today()));
  const [customTo, setCustomTo] = useState(toISODate(today()));
  const [video, setVideo] = useState(EMPTY);
  const [image, setImage] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [dayItems, setDayItems] = useState([]);
  const [dayTotal, setDayTotal] = useState({ total_ar: 0, total_count: 0 });
  const [loadingDays, setLoadingDays] = useState(false);

  const [sbItems, setSbItems] = useState([]);
  const [sbTotal, setSbTotal] = useState({ total_ar: 0, total_scenes: 0 });
  const [loadingSb, setLoadingSb] = useState(false);
  const [sbLoaded, setSbLoaded] = useState(false);

  const activeRange = useCallback(() => {
    const p = RANGE_PRESETS.find(r => r.id === preset);
    if (p && p.range) return p.range();
    return [customFrom, customTo];
  }, [preset, customFrom, customTo]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [from, to] = activeRange();
      const res = await fetch(`${API_URL}/api/ia-studio/my-consumption-stats?from=${from}&to=${to}`, { headers: authHeaders });
      const data = await res.json();
      if (data.success) { setVideo(data.video); setImage(data.image); }
      else setError(data.error || 'Erreur de chargement');
    } catch (e) {
      setError('Erreur réseau');
    }
    setLoading(false);
  }, [activeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadByDate = useCallback(async () => {
    setLoadingDays(true);
    try {
      const [from, to] = activeRange();
      const res = await fetch(`${API_URL}/api/ia-studio/video/stats/by-date?from=${from}&to=${to}`, { headers: authHeaders });
      const data = await res.json();
      if (data.success) { setDayItems(data.days); setDayTotal({ total_ar: data.total_ar, total_count: data.total_count }); }
    } catch (e) { /* silencieux */ }
    setLoadingDays(false);
  }, [activeRange]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadByStoryboard = useCallback(async () => {
    setLoadingSb(true);
    try {
      const res = await fetch(`${API_URL}/api/ia-studio/video/stats/by-storyboard`, { headers: authHeaders });
      const data = await res.json();
      if (data.success) { setSbItems(data.items); setSbTotal({ total_ar: data.total_ar, total_scenes: data.total_scenes }); }
    } catch (e) { /* silencieux */ }
    setLoadingSb(false);
    setSbLoaded(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (tab === 'overview') load(); }, [load, tab]);
  useEffect(() => { if (tab === 'by-date') loadByDate(); }, [loadByDate, tab]);
  useEffect(() => { if (tab === 'by-storyboard' && !sbLoaded) loadByStoryboard(); }, [tab, sbLoaded, loadByStoryboard]);

  const totalCount = video.count + image.count;
  const totalNet = video.net_ar + image.net_ar;
  const [from, to] = activeRange();

  const TABS = [
    { id: 'overview', label: 'Vue d’ensemble', icon: BarChart3 },
    { id: 'by-date', label: 'Par date', icon: Calendar },
    ...(showStoryboards ? [{ id: 'by-storyboard', label: 'Par storyboard', icon: Clapperboard }] : []),
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#140a24] border border-purple-500/25 rounded-2xl max-w-2xl w-full p-5 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-white font-bold text-base flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-300" /> Mes statistiques
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>

        <div className="flex gap-1 bg-black/20 border border-white/10 rounded-xl p-1 mb-4">
          {TABS.map(t => (
            <button
              key={t.id} onClick={() => setTab(t.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition ${
                tab === t.id ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-[0_0_14px_rgba(245,158,11,0.35)]' : 'text-gray-400 hover:text-gray-200'
              }`}
            >
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {(tab === 'overview' || tab === 'by-date') && (
          <>
            <div className="flex flex-wrap gap-1.5 mb-3">
              {RANGE_PRESETS.map(p => (
                <button
                  key={p.id} onClick={() => setPreset(p.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                    preset === p.id ? 'bg-white/15 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                  }`}
                >
                  {p.label}
                </button>
              ))}
              <button
                onClick={tab === 'overview' ? load : loadByDate}
                disabled={tab === 'overview' ? loading : loadingDays}
                className="ml-auto w-7 h-7 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center disabled:opacity-40"
              >
                {(tab === 'overview' ? loading : loadingDays)
                  ? <Loader2 className="w-3.5 h-3.5 text-gray-300 animate-spin" />
                  : <RefreshCw className="w-3.5 h-3.5 text-gray-300" />}
              </button>
            </div>

            {preset === 'custom' && (
              <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                <label className="text-gray-400 font-semibold">Du</label>
                <input
                  type="date" value={customFrom} max={customTo} onChange={e => setCustomFrom(e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-white focus:outline-none focus:border-amber-400"
                />
                <label className="text-gray-400 font-semibold">Au</label>
                <input
                  type="date" value={customTo} min={customFrom} max={toISODate(today())} onChange={e => setCustomTo(e.target.value)}
                  className="bg-white/5 border border-white/10 rounded-lg px-2 py-1 text-white focus:outline-none focus:border-amber-400"
                />
              </div>
            )}
            <p className="text-gray-500 text-[11px] mb-4">
              Période : <span className="text-gray-300 font-semibold">{from}</span> → <span className="text-gray-300 font-semibold">{to}</span>
            </p>
          </>
        )}

        {error && <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3 text-red-300 text-xs mb-3">{error}</div>}

        {tab === 'overview' && (
          loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>
          ) : (
            <>
              <div className="mb-3">
                <StatCard icon={Coins} title="Total consommé" count={totalCount} netAr={totalNet} gradient="from-amber-500 via-orange-500 to-rose-600" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <StatCard icon={Film} title="Vidéo" count={video.count} netAr={video.net_ar} gradient="from-fuchsia-600 via-purple-600 to-indigo-700" />
                <StatCard icon={ImageIcon} title="Image" count={image.count} netAr={image.net_ar} gradient="from-cyan-500 via-blue-600 to-indigo-700" />
              </div>

              {(video.refunded_ar > 0 || image.refunded_ar > 0) && (
                <div className="mt-3 bg-white/5 border border-white/10 rounded-xl p-3 text-[11px] text-gray-400 flex items-center gap-2">
                  <TrendingDown className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                  <span>{(video.refunded_ar + image.refunded_ar).toLocaleString('fr-FR')} Ar remboursés (générations échouées) sur cette période.</span>
                </div>
              )}
              <div className="mt-2 flex items-center gap-2 text-[11px] text-gray-500">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" />
                <span>{(video.charged_ar + image.charged_ar).toLocaleString('fr-FR')} Ar facturés au total sur cette période.</span>
              </div>
            </>
          )
        )}

        {tab === 'by-date' && (
          loadingDays ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>
          ) : dayItems.length === 0 ? (
            <div className="text-center text-gray-500 text-xs py-10 flex flex-col items-center gap-2">
              <LayoutGrid className="w-8 h-8 text-gray-700" /> Aucune génération sur cette période.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="text-gray-400 text-xs font-semibold">{dayTotal.total_count} génération{dayTotal.total_count > 1 ? 's' : ''}</span>
                <span className="text-amber-300 font-bold text-sm">{formatAr(dayTotal.total_ar)}</span>
              </div>
              <div className="space-y-2.5">
                {dayItems.map(d => <DayCard key={d.date} day={d} />)}
              </div>
            </>
          )
        )}

        {tab === 'by-storyboard' && (
          loadingSb ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-6 h-6 text-amber-400 animate-spin" /></div>
          ) : sbItems.length === 0 ? (
            <div className="text-center text-gray-500 text-xs py-10 flex flex-col items-center gap-2">
              <Clapperboard className="w-8 h-8 text-gray-700" /> Aucun storyboard pour l&apos;instant.
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-3 px-1">
                <span className="text-gray-400 text-xs font-semibold">{sbItems.length} storyboard{sbItems.length > 1 ? 's' : ''} · {sbTotal.total_scenes} scène{sbTotal.total_scenes > 1 ? 's' : ''}</span>
                <span className="text-amber-300 font-bold text-sm">{formatAr(sbTotal.total_ar)}</span>
              </div>
              <div className="space-y-2.5">
                {sbItems.map(it => <StoryboardCard key={it.storyboard_id} item={it} />)}
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}
